import { useSyncExternalStore } from 'react'

/**
 * Creates a module-level, localStorage-backed store exposed as a `useState`-shaped hook: one shared
 * value per key across every consumer (no prop-drilling), persisted per-browser and resilient to
 * disabled storage (private mode) — the in-memory value still updates, only persistence is dropped.
 *
 * `parse` maps the raw stored string (or `null` on first read / disabled storage) to the value, so
 * `parse(null)` defines the default; `serialize` maps it back. Extracted so preference toggles share
 * one implementation instead of re-hand-rolling the subscribe/getSnapshot/listeners boilerplate.
 */
export const createPersistedStore = <T>(key: string, parse: (raw: string | null) => T, serialize: (value: T) => string): (() => [T, (value: T) => void]) => {
    const read = (): T => {
        try {
            return parse(localStorage.getItem(key))
        } catch {
            return parse(null)
        }
    }

    let current = read()
    const listeners = new Set<() => void>()

    const subscribe = (onStoreChange: () => void): (() => void) => {
        listeners.add(onStoreChange)
        return () => {
            listeners.delete(onStoreChange)
        }
    }

    const getSnapshot = (): T => current

    const setValue = (value: T): void => {
        if (value === current) return
        current = value
        try {
            localStorage.setItem(key, serialize(value))
        } catch {
            // Private mode / storage disabled — keep the in-memory choice, drop persistence.
        }
        listeners.forEach((notify) => notify())
    }

    return () => {
        const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
        return [value, setValue]
    }
}
